// src/theme/index.js
import { alpha, createTheme } from '@mui/material/styles';
import shadows from './shadows';
import { light, dark } from './palette';

/**
 * The face the app ships. `components/FontLab.js` can override this at runtime
 * in local and development builds -- that panel is how a replacement gets
 * judged, but this line is still the only thing that decides what users get.
 */
const FONT_FAMILY =
  '"Open Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';

/**
 * Set this to pair a separate face with the body face above and h1-h6 will
 * take it. `null` keeps the app on one family, which is what it ships.
 */
const HEADING_FONT_FAMILY = null;

/** The variants `HEADING_FONT_FAMILY` applies to. */
const HEADING_VARIANTS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

/** The base size the scale below is drawn against. */
const BASE_FONT_SIZE = 13;

const HTML_FONT_SIZE = 16;

/**
 * The Indigo / Inter type scale.
 *
 * Sizes are px at the default base, fitted to what each variant already
 * rendered at before the scale existed -- `body2` was 13.0px and stays 13,
 * `body1` was 14.9 and becomes 15, `h6` was 17.3 and becomes 17. That is
 * deliberate: an earlier pass deferred this work because a fresh scale would
 * have reflowed ~800 call sites, so this one is fitted to the app rather than
 * imposed on it. Only two things move on purpose:
 *
 *   - `caption` 11.1px -> 12px. It was below the 12px floor and is the third
 *     most-used variant in the app (324 call sites).
 *   - h1-h6 no longer grow across breakpoints. `responsiveFontSizes()` used to
 *     stretch `h4` from 23.8px to 32.4px at >=1200px, so a heading rendered at
 *     a different size than the developer saw -- which is exactly why 196
 *     hardcoded `fontSize` overrides accumulated. One size per variant now.
 */
const TYPE_SCALE = {
  h1: 44,
  h2: 36,
  // Page title -- "Deals", "Reports", the h1 of a screen.
  h3: 30,
  // Section titles.
  h4: 24,
  h5: 20,
  // Card and group titles.
  h6: 17,
  subtitle1: 15,
  subtitle2: 13,
  // Body and table text.
  body1: 15,
  body2: 13,
  button: 13,
  caption: 12,
  overline: 12,
  // Long-form reading, not app chrome -- the integration help guides and
  // anything else that is a page of prose rather than a dense screen. 15px is
  // right for a table row and too tight for six paragraphs in a row, which is
  // why those pages had drifted to a scattered `fontSize: '1.05rem'` (51 call
  // sites across two files). One named role instead.
  prose: 16,
};

/**
 * Weight carries the hierarchy that the size steps alone cannot: h5 and h6 are
 * only 3px apart, so without this they would read as the same level. Three
 * sanctioned weights, per `docs/design-tokens.md` -- 400 body, 600 heading and
 * emphasis, 700 strong.
 */
const TYPE_WEIGHT = {
  h1: 700,
  h2: 700,
  h3: 700,
  h4: 600,
  h5: 600,
  h6: 600,
  subtitle1: 600,
  subtitle2: 600,
  body1: 400,
  body2: 400,
  button: 700,
  caption: 500,
  overline: 700,
  prose: 400,
};

const TYPE_LINE_HEIGHT = {
  h1: 1.12,
  h2: 1.14,
  h3: 1.15,
  h4: 1.2,
  h5: 1.25,
  h6: 1.3,
  subtitle1: 1.45,
  subtitle2: 1.45,
  body1: 1.5,
  body2: 1.5,
  button: 1.2,
  caption: 1.35,
  overline: 1.2,
  // Looser than body: long measures need more leading to stay readable.
  prose: 1.65,
};

/**
 * Build the variant definitions for a given base size.
 *
 * Sizes are emitted as `rem` scaled by `base / BASE_FONT_SIZE`, not as fixed
 * `px`. That matters: MUI's `typography.fontSize` option only scales the
 * variants it computes itself, so once a variant carries an explicit
 * `fontSize` the base-size control in `components/ThemeConfigurator.js` would
 * otherwise stop moving it. Scaling here keeps that control working, and keeps
 * the whole scale responsive to the browser's own font-size setting.
 *
 * Letter spacing stays at the font default throughout, per the spec; the one
 * exception is `overline`, a compact uppercase label, where the spec asks for
 * tracking.
 */
const buildTypographyScale = (base = BASE_FONT_SIZE, headingFamily) => {
  const coefficient = base / BASE_FONT_SIZE;
  const rem = (px) => `${((px / HTML_FONT_SIZE) * coefficient).toFixed(4)}rem`;

  return Object.keys(TYPE_SCALE).reduce((acc, variant) => {
    acc[variant] = {
      fontSize: rem(TYPE_SCALE[variant]),
      fontWeight: TYPE_WEIGHT[variant],
      lineHeight: TYPE_LINE_HEIGHT[variant],
      ...(headingFamily && HEADING_VARIANTS.includes(variant)
        ? { fontFamily: headingFamily }
        : {}),
      ...(variant === 'button' ? { textTransform: 'none' } : {}),
      ...(variant === 'overline'
        ? { letterSpacing: '0.08em', textTransform: 'uppercase' }
        : {}),
    };
    return acc;
  }, {});
};

/**
 * The token names the app can reach from a plain string `sx` value, e.g.
 * `border: '1px solid var(--dc-border)'`. Emitted onto `:root` so they follow
 * the active theme mode without every call site needing `theme` in scope.
 */
/**
 * Corner radius, as four steps rather than whatever number each call site
 * reached for. `sm`/`md`/`lg` are what 94% of the app already used -- `sx`
 * multiplies by MUI's 4px base, so `borderRadius: 1 | 2 | 3` lands on them --
 * and `pill` replaces the 13, 20, 25, 40 and '20px' that all meant "fully
 * rounded" on boxes of different heights.
 *
 *   sm   4px   inline chrome: badges, tags, the smallest controls
 *   md   8px   controls and surfaces: buttons, inputs, cards, menus
 *   lg  12px   page-level panels and dialogs
 *   pill        anything whose height should decide its corners
 *
 * Reach for them as `var(--dc-radius-*)`, or keep using the `sx` multiples
 * where a number is already clear.
 */
export const RADIUS = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  pill: '999px',
};

const cssVariables = (tokens) => ({
  '--dc-primary': tokens.primary,
  // Indigo as *text* -- links, selected tabs, anything reading against a
  // surface rather than filling one. See the note in `palette.js`.
  '--dc-primary-text': tokens.primaryText,
  '--dc-primary-hover': tokens.primaryHover,
  '--dc-primary-active': tokens.primaryActive,
  '--dc-primary-soft': tokens.primarySoft,
  '--dc-primary-subtle': tokens.primarySubtle,
  '--dc-background': tokens.background,
  '--dc-sidebar': tokens.sidebar,
  '--dc-card': tokens.card,
  '--dc-surface-raised': tokens.surfaceRaised,
  '--dc-border': tokens.border,
  '--dc-border-strong': tokens.borderStrong,
  '--dc-divider': tokens.divider,
  '--dc-heading': tokens.heading,
  '--dc-text': tokens.text,
  '--dc-text-secondary': tokens.textSecondary,
  '--dc-text-muted': tokens.textMuted,
  '--dc-text-disabled': tokens.textDisabled,
  '--dc-table-header': tokens.tableHeader,
  '--dc-row-hover': tokens.rowHover,
  '--dc-selected-row': tokens.selectedRow,
  '--dc-input-background': tokens.inputBackground,
  '--dc-logo-filter': tokens.logoFilter,
  '--dc-mono-icon-filter': tokens.monoIconFilter,
  '--dc-scrollbar': tokens.scrollbar,
  '--dc-success': tokens.success,
  '--dc-warning': tokens.warning,
  '--dc-danger': tokens.danger,
  '--dc-radius-sm': RADIUS.sm,
  '--dc-radius-md': RADIUS.md,
  '--dc-radius-lg': RADIUS.lg,
  '--dc-radius-pill': RADIUS.pill,
});

const getTheme = (mode, customTheme = {}) => {
  // Create a deep copy to avoid mutating the original palette imports
  const basePalette = JSON.parse(
    JSON.stringify(mode === 'light' ? light : dark),
  );

  // A colour preset from the dev theme panel: raw tokens for this mode, merged
  // before the single-colour overrides below so a picker still wins over it.
  const presetTokens = customTheme.colorTokens?.[mode];
  if (presetTokens) {
    const { primaryLight, ...tokens } = presetTokens;
    const t = { ...basePalette.custom, ...tokens };
    basePalette.custom = t;
    basePalette.primary.main = t.primary;
    basePalette.primary.dark = t.primaryHover;
    basePalette.primary.light = primaryLight || t.primaryActive;
    basePalette.info = {
      ...basePalette.info,
      main: t.primary,
      dark: t.primaryHover,
      light: basePalette.primary.light,
    };
    basePalette.text = {
      ...basePalette.text,
      primary: t.text,
      secondary: t.textSecondary,
      disabled: t.textDisabled,
      blue: t.primaryText,
      muted: t.textMuted,
    };
    basePalette.divider = t.divider;
    basePalette.background = {
      ...basePalette.background,
      paper: t.card,
      default: t.background,
      level1: t.border,
      level2: t.tableHeader,
    };
    basePalette.alternate =
      mode === 'light'
        ? { main: t.background, dark: t.primarySoft }
        : { main: t.sidebar, dark: t.background };
  }

  // Apply custom theme overrides
  if (customTheme.primary) {
    basePalette.primary.main = customTheme.primary;
    basePalette.custom.primary = customTheme.primary;
    // A custom brand colour has to carry the text role too, or links and
    // selected tabs would keep rendering the stock indigo. In dark mode the
    // default `primaryText` is a deliberately lifted tint rather than the
    // fill, so only overwrite it when the caller has not supplied its own.
    basePalette.custom.primaryText =
      customTheme.primaryText || customTheme.primary;
    basePalette.text.blue = basePalette.custom.primaryText;
  }
  if (customTheme.secondary) {
    basePalette.secondary.main = customTheme.secondary;
  }
  if (customTheme.primaryContrast) {
    basePalette.primary.contrastText = customTheme.primaryContrast;
  }
  if (customTheme.primaryDark) {
    basePalette.primary.dark = customTheme.primaryDark;
    basePalette.custom.primaryHover = customTheme.primaryDark;
  }
  if (customTheme.primaryLight) {
    basePalette.primary.light = customTheme.primaryLight;
  }
  if (
    customTheme.success ||
    customTheme.error ||
    customTheme.info ||
    customTheme.warning
  ) {
    basePalette.success = {
      ...basePalette.success,
      main: customTheme.success || basePalette.success.main,
    };
    basePalette.error = {
      ...basePalette.error,
      main: customTheme.error || basePalette.error.main,
    };
    basePalette.info = {
      ...basePalette.info,
      main: customTheme.info || basePalette.info.main,
    };
    basePalette.warning = {
      ...basePalette.warning,
      main: customTheme.warning || basePalette.warning.main,
    };
    basePalette.custom = {
      ...basePalette.custom,
      success: customTheme.success || basePalette.custom.success,
      danger: customTheme.error || basePalette.custom.danger,
      warning: customTheme.warning || basePalette.custom.warning,
    };
  }
  if (customTheme.textPrimary || customTheme.textSecondary) {
    basePalette.text = {
      ...basePalette.text,
      primary: customTheme.textPrimary || basePalette.text.primary,
      secondary: customTheme.textSecondary || basePalette.text.secondary,
    };
    basePalette.custom = {
      ...basePalette.custom,
      text: customTheme.textPrimary || basePalette.custom.text,
      textSecondary:
        customTheme.textSecondary || basePalette.custom.textSecondary,
    };
  }

  const tokens = basePalette.custom;

  // The scrollbar thumb is its own palette token, per mode.
  //
  // The override is per mode: a bar that reads well on the light page is
  // invisible on the dark one, so the panel sets `scrollbarLight` and
  // `scrollbarDark` separately and only the active mode's value applies.
  tokens.scrollbar =
    (mode === 'light'
      ? customTheme.scrollbarLight
      : customTheme.scrollbarDark) || tokens.scrollbar;

  // `fontFamily` / `headingFontFamily` are only ever set by the dev-only font
  // panel; production always falls through to the shipping constant.
  const bodyFamily = customTheme.fontFamily || FONT_FAMILY;
  const headingFamily = customTheme.headingFontFamily || HEADING_FONT_FAMILY;

  const baseTheme = {
    palette: basePalette,
    shadows: shadows(mode),
    typography: {
      fontFamily: bodyFamily,
      fontSize: customTheme.fontSize || BASE_FONT_SIZE,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      ...buildTypographyScale(
        customTheme.fontSize || BASE_FONT_SIZE,
        headingFamily,
      ),
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': cssVariables(tokens),
          html: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          body: {
            fontFamily: bodyFamily,
            backgroundColor: tokens.background,
            color: tokens.text,
          },
          // Chrome/Safari/Edge. Scoped to `*` so panes, dialogs and tables get
          // the same bar as the page. A component that paints its own
          // ::-webkit-scrollbar still wins on specificity.
          //
          // These pseudo-element rules are what force a classic, always-present
          // bar instead of the macOS overlay one that fades out when idle. They
          // are also why `scrollbar-width` / `scrollbar-color` must not be set
          // here: both are *inherited*, and Chromium drops every
          // ::-webkit-scrollbar-* rule on an element that carries either one —
          // setting them on `html` silently disabled custom scrollbars for the
          // whole app, the sidebar's included. Firefox, which has no
          // pseudo-elements to offer, gets them behind its own `@supports`
          // probe below.
          '*::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: tokens.scrollbar,
            borderRadius: 8,
          },
          '*::-webkit-scrollbar-corner': {
            backgroundColor: 'transparent',
          },
          // React Flow's zoom/fit/lock controls (the org charts) ship with a
          // hard-coded near-white background and black icons. Prefixed with
          // `.react-flow` so it outranks reactflow/dist/style.css whichever
          // stylesheet lands later.
          '.react-flow .react-flow__controls': {
            boxShadow: `0 0 0 1px ${tokens.border}`,
          },
          '.react-flow .react-flow__controls-button': {
            backgroundColor: tokens.card,
            borderBottomColor: tokens.border,
            color: tokens.text,
            fill: 'currentColor',
          },
          '.react-flow .react-flow__controls-button:hover': {
            backgroundColor: tokens.rowHover,
          },
          '@supports (-moz-appearance: none)': {
            html: {
              scrollbarWidth: 'thin',
              scrollbarColor: `${tokens.scrollbar} transparent`,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: RADIUS.md,
            backgroundColor: mode === 'light' ? tokens.heading : '#0F1218',
            color: '#FFFFFF',
            fontWeight: 500,
          },
          arrow: {
            color: mode === 'light' ? tokens.heading : '#0F1218',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            input: {
              '&:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 100px ${tokens.card} inset`,
                WebkitTextFillColor: tokens.text,
              },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            borderRadius: 8,
            paddingTop: 8,
            paddingBottom: 8,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          containedPrimary: {
            backgroundColor: tokens.primary,
            color: '#FFFFFF',
            border: `1px solid ${tokens.primary}`,
            '&:hover': {
              backgroundColor: tokens.primaryHover,
              borderColor: tokens.primaryHover,
            },
            '&:active': {
              backgroundColor: tokens.primaryActive,
              borderColor: tokens.primaryActive,
            },
          },
          containedSecondary: mode === 'light' ? { color: 'white' } : {},
          // The spec's "Secondary" button: a card-coloured surface with the
          // ordinary border, not a tinted one.
          outlined: {
            backgroundColor: tokens.card,
            color: tokens.text,
            borderColor: tokens.border,
            '&:hover': {
              backgroundColor: tokens.primarySubtle,
              borderColor: tokens.border,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: tokens.inputBackground,
            color: tokens.text,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.primary,
            },
            // The spec's focus state is a 1px indigo edge plus a 3px soft ring.
            // MUI's own focus treatment is a 2px notched outline, so the width
            // is pinned back to 1px and the ring is drawn as a box-shadow.
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(tokens.primary, 0.14)}`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
              borderColor: tokens.primary,
            },
          },
          input: {
            borderRadius: 8,
            '&::placeholder': {
              color: tokens.textMuted,
              opacity: 1,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md,
            // MUI paints an alpha-white gradient over dark-mode Paper to fake
            // elevation. Indigo / Inter rules out gradients, and the overlay
            // pushed cards off the #222631 token.
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: RADIUS.lg,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.card,
            border: `1px solid ${tokens.border}`,
            borderRadius: 8,
            boxShadow:
              mode === 'light'
                ? '0 8px 24px rgba(39, 48, 71, 0.06)'
                : '0 8px 24px rgba(0, 0, 0, 0.28)',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${tokens.border}`,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            border: `1px solid ${tokens.border}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            // Only the rule colour is global: the `--sidebar` surface belongs
            // to the dashboard sidebar (see layouts/Dashboard/.../Sidebar.js),
            // not to every slide-over drawer in the app.
            borderColor: tokens.border,
            // A drawer is flush to the edge of the viewport, so it opts out of
            // the radius `MuiPaper` now gives every other surface.
            borderRadius: 0,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: tokens.divider,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontWeight: 400,
            color: tokens.text,
            borderBottom: `1px solid ${tokens.border}`,
          },
          head: {
            backgroundColor: tokens.tableHeader,
            color: tokens.textSecondary,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            // Header labels across the app are wrapped in their own
            // `<Typography variant="body2">`, which would otherwise reset the
            // cell's weight and tracking. Two classes deep, so this still
            // beats the variant class without needing `!important`.
            '& .MuiTypography-root': {
              fontWeight: 700,
              letterSpacing: '0.06em',
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': {
              backgroundColor: tokens.rowHover,
            },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: tokens.selectedRow,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: tokens.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            textTransform: 'none',
            color: tokens.textSecondary,
            '&:hover': { color: tokens.text },
            '&.Mui-selected': { color: tokens.primaryText },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            // Avatars read as part of the surface they sit on rather than as a
            // coloured badge, with the body text colour on them for the
            // initials MUI falls back to when there is no image.
            //
            // Dark mode can use the card fill directly: it lifts 1.15:1 off
            // the page, so the disc is visible on its own. The light card
            // cannot -- it sits within 1.04:1 of both the page and a card, so
            // an avatar on either surface disappears entirely. Light mode
            // therefore takes the quiet fill the palette already sanctions for
            // this (`border`, mapped to `background.level1`), which lands at
            // 1.23:1 on a card and keeps the initials at 5.47:1.
            backgroundColor: mode === 'light' ? tokens.border : tokens.card,
            color: tokens.text,
            // The fill alone cannot carry the shape everywhere -- an avatar
            // dropped on a surface tinted like its own fill (a deal node, a
            // selected row) loses its edge. `borderStrong` is a step above
            // both fills, so the disc stays legible whatever it sits on.
            border: `1px solid ${tokens.borderStrong}`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            // A chip's height decides its corners, whatever size it is.
            borderRadius: RADIUS.pill,
          },
          outlined: {
            borderColor: tokens.border,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: tokens.primaryText,
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          // `prose` is a custom variant, and MUI renders any variant it does
          // not recognise as a <span>. Map it to a paragraph so help pages
          // stay semantic (and so block spacing behaves).
          variantMapping: {
            prose: 'p',
          },
        },
      },
    },
  };

  // Deliberately not wrapped in `responsiveFontSizes()`: the scale in
  // `TYPE_SCALE` is fixed at every width. See the comment on TYPE_SCALE.
  return createTheme(baseTheme);
};

/**
 * The scrollbar colour a mode starts from, for the theme panel's pickers: the
 * inactive mode's value is not on the live theme object to read back.
 */
export const defaultScrollbarColor = (mode) =>
  (mode === 'dark' ? dark : light).custom.scrollbar;

export default getTheme;
